// PDF utilities for the Arbeidsvoorwaarden Check 2026 scorecard.
// Style-consistent met /lib/pdfGenerator.js (Vitaliteit Benchmark).

import jsPDF from 'jspdf';
import QRCode from 'qrcode';

const fmtNum = (n) =>
  new Intl.NumberFormat('nl-NL', { maximumFractionDigits: 1 }).format(n || 0);

function drawFooter(doc, W, H, MARGIN_X, reportId, generatedAt) {
  const CONTENT_W = W - MARGIN_X * 2;
  doc.setDrawColor(226, 232, 240);
  doc.line(MARGIN_X, H - 50, W - MARGIN_X, H - 50);
  doc.setFontSize(7.5);
  doc.setTextColor(100, 116, 139);
  const disclaimer = doc.splitTextToSize(
    'Disclaimer: deze scorecard is indicatief en gebaseerd op publieke onderzoeksdata over werknemerstevredenheid en de Gen Z talent-benchmark 2026. De resultaten zijn bedoeld als richtlijn en kunnen geen exact beeld geven van uw individuele situatie. Voor een onderbouwd talent-strategie advies op maat: neem contact op met Zynq.',
    CONTENT_W
  );
  doc.text(disclaimer, MARGIN_X, H - 42);
  doc.setFontSize(7);
  doc.setTextColor(148, 163, 184);
  doc.text(
    `Rapport-ID: ${reportId}  |  Bron: Gen Z Talent Benchmark 2026  |  Zynq Arbeidsvoorwaarden Check  |  Gegenereerd: ${generatedAt.toLocaleString('nl-NL')}`,
    MARGIN_X,
    H - 18
  );
  const pageNum = doc.internal.getCurrentPageInfo().pageNumber;
  doc.text(`Pagina ${pageNum}`, W - MARGIN_X, H - 18, { align: 'right' });
}

export async function generateArbeidsvoorwaardenPdf({
  company,
  sector,
  contactName,
  contactEmail,
  checkedItems,
  allItems,
  scoreData,
  benchmark,
  submissionId,
  cfg,
}) {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const MARGIN_X = 40;
  const CONTENT_W = W - MARGIN_X * 2;
  const FOOTER_TOP = H - 60;

  const reportId =
    submissionId || `AV-${new Date().getTime().toString(36).toUpperCase()}`;
  const generatedAt = new Date();

  let y = 50;

  const ensureSpace = (needed) => {
    if (y + needed > FOOTER_TOP) {
      drawFooter(doc, W, H, MARGIN_X, reportId, generatedAt);
      doc.addPage();
      y = 60;
    }
  };

  const sectionHeader = (title) => {
    ensureSpace(40);
    doc.setTextColor(30, 41, 59);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text(title, MARGIN_X, y);
    y += 8;
    doc.setDrawColor(226, 232, 240);
    doc.line(MARGIN_X, y, W - MARGIN_X, y);
    y += 16;
  };

  // ── Header band ──
  doc.setFillColor(16, 185, 129);
  doc.rect(0, 0, W, 70, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text('Arbeidsvoorwaarden Scorecard 2026', MARGIN_X, 44);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('Zynq - vitaliteit als missie  |  Gen Z Talent Benchmark', MARGIN_X, 60);
  doc.setFontSize(8);
  doc.text(`Rapport-ID: ${reportId}`, W - MARGIN_X, 60, { align: 'right' });

  y = 95;
  doc.setTextColor(30, 41, 59);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(`Scorecard voor ${company}`, MARGIN_X, y);
  y += 16;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 116, 139);
  doc.text(`Sector: ${sector}`, MARGIN_X, y);
  y += 12;
  doc.text(
    `Datum: ${generatedAt.toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })}  |  Contact: ${contactName} <${contactEmail}>`,
    MARGIN_X,
    y
  );
  y += 18;

  // ── Wellness Grade hero box ──
  ensureSpace(140);
  const gradeColor =
    scoreData.grade.startsWith('A')
      ? [6, 95, 70]
      : scoreData.grade === 'B'
      ? [4, 120, 87]
      : scoreData.grade === 'C'
      ? [217, 119, 6]
      : [185, 28, 28];
  doc.setFillColor(236, 253, 245);
  doc.setDrawColor(16, 185, 129);
  doc.roundedRect(MARGIN_X, y, CONTENT_W, 130, 8, 8, 'FD');

  // Grote grade
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 116, 139);
  doc.text('WELLNESS GRADE', MARGIN_X + 24, y + 26);
  doc.setFontSize(64);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...gradeColor);
  doc.text(scoreData.grade, MARGIN_X + 24, y + 90);

  // Score data rechts
  const rightX = MARGIN_X + 180;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 116, 139);
  doc.text('TOTAAL SCORE', rightX, y + 26);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(6, 95, 70);
  doc.text(`${scoreData.percentage}%`, rightX, y + 46);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 116, 139);
  doc.text(
    `${fmtNum(scoreData.totalPoints)} / ${fmtNum(scoreData.maxPoints)} punten`,
    rightX,
    y + 60
  );

  // Mini scores per categorie
  doc.setFontSize(8);
  doc.setTextColor(71, 85, 105);
  doc.text(
    `Mentale Gezondheid:  ${scoreData.byCategory.mental.checked}/${scoreData.byCategory.mental.total} items  (${scoreData.byCategory.mental.percentage}%)`,
    rightX,
    y + 82
  );
  doc.text(
    `Flexibiliteit:                 ${scoreData.byCategory.flexibility.checked}/${scoreData.byCategory.flexibility.total} items  (${scoreData.byCategory.flexibility.percentage}%)`,
    rightX,
    y + 96
  );
  doc.text(
    `Fysiek & Omgeving:    ${scoreData.byCategory.physical.checked}/${scoreData.byCategory.physical.total} items  (${scoreData.byCategory.physical.percentage}%)`,
    rightX,
    y + 110
  );

  y += 142;

  // ── Beschrijving van de grade ──
  ensureSpace(60);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(51, 65, 85);
  const verdictLines = doc.splitTextToSize(scoreData.verdict, CONTENT_W);
  doc.text(verdictLines, MARGIN_X, y);
  y += verdictLines.length * 13 + 12;

  // ── Gen Z Benchmark 2026 vergelijking ──
  sectionHeader('Vergelijking met Gen Z Benchmark 2026');
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 116, 139);
  const benchIntro = doc.splitTextToSize(
    'De Gen Z Benchmark 2026 weerspiegelt de harde eisen die de instromende generatie toptalent stelt aan werkgevers. 85% beschouwt mentale steun en 90% beschouwt verregaande flexibiliteit als een non-negotiable bij de keuze van een werkgever.',
    CONTENT_W
  );
  doc.text(benchIntro, MARGIN_X, y);
  y += benchIntro.length * 11 + 10;

  // Bars
  const drawCompareBar = (label, you, target) => {
    ensureSpace(50);
    const maxVal = 100;
    const barX = MARGIN_X + 160;
    const barW = CONTENT_W - 170;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(51, 65, 85);
    doc.text(label, MARGIN_X, y + 11);
    // Background
    doc.setFillColor(241, 245, 249);
    doc.roundedRect(barX, y + 4, barW, 14, 3, 3, 'F');
    // You bar
    const youW = Math.max(2, Math.min(barW, (you / maxVal) * barW));
    const color = you >= target ? [16, 185, 129] : you >= target * 0.6 ? [245, 158, 11] : [239, 68, 68];
    doc.setFillColor(...color);
    doc.roundedRect(barX, y + 4, youW, 14, 3, 3, 'F');
    // Target marker (Gen Z eis)
    const targetX = barX + (target / maxVal) * barW;
    doc.setDrawColor(13, 148, 136);
    doc.setLineDashPattern([3, 2], 0);
    doc.setLineWidth(1.2);
    doc.line(targetX, y + 1, targetX, y + 22);
    doc.setLineDashPattern([], 0);
    doc.setLineWidth(0.4);
    // Labels
    doc.setFontSize(7.5);
    doc.setTextColor(100, 116, 139);
    doc.text(`Jouw: ${you}%`, barX, y + 32);
    doc.text(`Gen Z eis: ${target}%`, barX + 70, y + 32);
    y += 42;
  };
  drawCompareBar('Mentale Gezondheid', scoreData.byCategory.mental.percentage, benchmark.mental);
  drawCompareBar('Flexibiliteit', scoreData.byCategory.flexibility.percentage, benchmark.flexibility);
  drawCompareBar('Fysiek & Omgeving', scoreData.byCategory.physical.percentage, benchmark.physical);
  y += 4;
  doc.setFontSize(7.5);
  doc.setTextColor(100, 116, 139);
  doc.text(
    'Legenda: gevulde balk = jouw aanbod  |  gestreepte lijn = harde eis van Gen Z talent (2026)',
    MARGIN_X,
    y
  );
  y += 14;

  // ── Gaps to close ──
  sectionHeader('Gaps to Close - direct toepasbaar advies');
  const missing = allItems.filter((it) => !checkedItems.includes(it.id));
  if (missing.length === 0) {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(6, 95, 70);
    doc.text(
      'Geen gaps. Jullie bieden alle ge-benchmarkte arbeidsvoorwaarden. Top!',
      MARGIN_X,
      y
    );
    y += 18;
  } else {
    // Sorteer op weight desc voor maximale impact
    const sorted = [...missing].sort((a, b) => b.weight - a.weight).slice(0, 8);
    sorted.forEach((it) => {
      const title = `${it.label}  (${it.category === 'mental' ? 'Mentaal' : it.category === 'flexibility' ? 'Flex' : 'Fysiek'})`;
      const body = it.advice;
      const txt = doc.splitTextToSize(title, CONTENT_W - 24);
      const bodyLines = doc.splitTextToSize(body, CONTENT_W - 24);
      const blockH = 6 + txt.length * 12 + bodyLines.length * 11 + 14;
      ensureSpace(blockH);
      const dotColor = it.category === 'mental' ? [219, 39, 119] : it.category === 'flexibility' ? [37, 99, 235] : [217, 119, 6];
      doc.setFillColor(...dotColor);
      doc.circle(MARGIN_X + 5, y + 6, 3, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(30, 41, 59);
      doc.text(txt, MARGIN_X + 18, y + 10);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(71, 85, 105);
      doc.text(bodyLines, MARGIN_X + 18, y + 10 + txt.length * 12);
      y += blockH;
    });
    if (missing.length > 8) {
      ensureSpace(20);
      doc.setFontSize(8.5);
      doc.setTextColor(100, 116, 139);
      doc.setFont('helvetica', 'italic');
      doc.text(
        `+ ${missing.length - 8} extra verbeterpunten. Bespreek deze in een strategiegesprek met Zynq.`,
        MARGIN_X,
        y
      );
      y += 14;
    }
  }
  y += 6;

  // ── CTA & QR ──
  try {
    const baseBookingUrl = (cfg?.booking_url || '').trim() || 'https://zynq.org/boeken/';
    const sep = baseBookingUrl.includes('?') ? '&' : '?';
    const ctaUrl = `${baseBookingUrl}${sep}ref=${encodeURIComponent(reportId)}&tool=arbeidsvoorwaarden`;
    const qrDataUrl = await QRCode.toDataURL(ctaUrl, {
      width: 200,
      margin: 0,
      color: { dark: '#065f46', light: '#ffffff' },
    });
    const qrSize = 70;
    ensureSpace(qrSize + 16);
    doc.setFillColor(248, 250, 252);
    doc.setDrawColor(226, 232, 240);
    doc.roundedRect(MARGIN_X, y, CONTENT_W, qrSize + 10, 6, 6, 'FD');
    doc.addImage(qrDataUrl, 'PNG', MARGIN_X + 8, y + 5, qrSize, qrSize);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(6, 95, 70);
    doc.text('Win de War on Talent - plan een strategiegesprek', MARGIN_X + qrSize + 24, y + 22);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(71, 85, 105);
    const ctaLines = doc.splitTextToSize(
      'Scan de QR-code voor een vrijblijvend talent-strategiegesprek. We helpen je een arbeidsvoorwaardenpakket te bouwen waar Gen Z toptalent niet omheen kan.',
      CONTENT_W - qrSize - 40
    );
    doc.text(ctaLines, MARGIN_X + qrSize + 24, y + 38);
    doc.setFontSize(7.5);
    doc.setTextColor(100, 116, 139);
    doc.text(ctaUrl, MARGIN_X + qrSize + 24, y + qrSize + 2);
    y += qrSize + 16;
  } catch {
    // QR optional
  }

  drawFooter(doc, W, H, MARGIN_X, reportId, generatedAt);

  const safeName = company.replace(/[^a-z0-9-_]/gi, '_').slice(0, 40) || 'bedrijf';
  doc.save(`Arbeidsvoorwaarden_Scorecard_2026_${safeName}.pdf`);
}
